const HOME_SSR_DIAGNOSTIC_PREFIX = "[HOME_SSR_DIAGNOSTIC]";
const HOME_SSR_DIAGNOSTIC_CONTEXT_KEY = "homeSsrDiagnostic";
const MAX_DIAGNOSTIC_TEXT_LENGTH = 500;
const MAX_DIAGNOSTIC_URL_LENGTH = 1_000;
const MAX_DIAGNOSTIC_STACK_LENGTH = 4_000;

type DiagnosticFields = Record<
  string,
  string | number | boolean | null | undefined
>;

type DiagnosticLevel = "error" | "info" | "warn";

/**
 * Non-sensitive identity fields used to compare request and atom state.
 *
 * @example
 * const identity = getSafeIdentityDiagnostic(user);
 */
export type SafeIdentityDiagnostic = {
  userId: string | number | null;
  userType: string | null;
  clientId: string | number | null;
  clientType: string | null;
};

/**
 * Request-scoped state shared by Remix loaders and the server renderer.
 *
 * @example
 * const diagnosticContext = getHomeSsrDiagnosticContext(loadContext, request);
 */
export type HomeSsrDiagnosticContext = {
  requestId: string;
  loggedEvents: Set<string>;
};

/**
 * Safe root-loader snapshot serialized for SSR state consistency checks.
 *
 * @example
 * return { diagnostic: { requestId, domain, user, settingsHost } };
 */
export type HomeSsrRootDiagnostic = {
  requestId: string;
  domain: string;
  user: SafeIdentityDiagnostic;
  settingsHost: string | null;
};

/**
 * Return the request diagnostic context, creating and attaching it when absent.
 *
 * @example
 * const diagnosticContext = getHomeSsrDiagnosticContext(context, request);
 */
export function getHomeSsrDiagnosticContext(
  loadContext: unknown,
  request: Request,
): HomeSsrDiagnosticContext {
  const mutableContext = asRecord(loadContext);
  const existingContext = mutableContext?.[HOME_SSR_DIAGNOSTIC_CONTEXT_KEY];

  if (isHomeSsrDiagnosticContext(existingContext)) {
    return existingContext;
  }

  const diagnosticContext: HomeSsrDiagnosticContext = {
    requestId: getRequestId(request),
    loggedEvents: new Set<string>(),
  };

  if (mutableContext) {
    mutableContext[HOME_SSR_DIAGNOSTIC_CONTEXT_KEY] = diagnosticContext;
  }

  return diagnosticContext;
}

/**
 * Emit one grep-friendly JSON diagnostic line containing bounded scalar fields.
 *
 * @example
 * logHomeSsrDiagnostic(context, "home_loader_started", { domain });
 */
export function logHomeSsrDiagnostic(
  diagnosticContext: Pick<HomeSsrDiagnosticContext, "requestId">,
  event: string,
  fields: DiagnosticFields = {},
  level: DiagnosticLevel = "info",
): void {
  const message = `${HOME_SSR_DIAGNOSTIC_PREFIX} ${JSON.stringify({
    timestamp: new Date().toISOString(),
    event,
    requestId: diagnosticContext.requestId,
    ...fields,
  })}`;

  if (level === "error") {
    console.error(message);

    return;
  }

  if (level === "warn") {
    console.warn(message);

    return;
  }

  console.info(message);
}

/**
 * Emit an event at most once for a request, even when several loaders share it.
 *
 * @example
 * logHomeSsrDiagnosticOnce(context, "request_received", requestFields);
 */
export function logHomeSsrDiagnosticOnce(
  diagnosticContext: HomeSsrDiagnosticContext,
  event: string,
  fields: DiagnosticFields = {},
  level: DiagnosticLevel = "info",
): void {
  if (diagnosticContext.loggedEvents.has(event)) return;

  diagnosticContext.loggedEvents.add(event);
  logHomeSsrDiagnostic(diagnosticContext, event, fields, level);
}

/**
 * Extract the host inputs used by Remix without logging query values or headers.
 *
 * @example
 * const requestFields = getSafeRequestDiagnostic(request);
 */
export function getSafeRequestDiagnostic(request: Request): DiagnosticFields {
  const url = new URL(request.url);

  return {
    requestUrl: `${url.origin}${url.pathname}`.slice(
      0,
      MAX_DIAGNOSTIC_URL_LENGTH,
    ),
    urlHost: url.host.slice(0, MAX_DIAGNOSTIC_TEXT_LENGTH),
    hostHeader: sanitizeHeaderValue(request.headers.get("host")),
    forwardedHostHeader: sanitizeHeaderValue(
      request.headers.get("x-forwarded-host"),
    ),
  };
}

/**
 * Extract identifiers and discriminator fields while excluding personal data.
 *
 * @example
 * const identity = getSafeIdentityDiagnostic(currentUser);
 */
export function getSafeIdentityDiagnostic(
  identity: unknown,
): SafeIdentityDiagnostic {
  const identityRecord = asRecord(identity);
  const clientRecord = asRecord(identityRecord?.client);

  return {
    userId: getSafeIdentifier(identityRecord?.id),
    userType: getSafeType(identityRecord?.userType ?? identityRecord?.type),
    clientId: getSafeIdentifier(clientRecord?.id ?? identityRecord?.clientId),
    clientType: getSafeType(clientRecord?.type ?? identityRecord?.clientType),
  };
}

/**
 * Extract status, method, path, code, and a redacted message from an error.
 *
 * @example
 * logHomeSsrDiagnostic(context, "api_error", getSafeErrorDiagnostic(error));
 */
export function getSafeErrorDiagnostic(error: unknown): DiagnosticFields {
  const errorRecord = asRecord(error);
  const responseRecord = asRecord(errorRecord?.response);
  const configRecord = asRecord(errorRecord?.config);

  return {
    errorName: sanitizeDiagnosticText(errorRecord?.name),
    errorMessage: sanitizeDiagnosticText(errorRecord?.message),
    errorStack: sanitizeDiagnosticText(
      errorRecord?.stack,
      MAX_DIAGNOSTIC_STACK_LENGTH,
    ),
    errorCode: sanitizeDiagnosticText(errorRecord?.code),
    responseStatus: getSafeNumber(
      responseRecord?.status ?? errorRecord?.status,
    ),
    requestMethod: sanitizeDiagnosticText(configRecord?.method)?.toUpperCase(),
    requestPath: getSafeRequestPath(configRecord?.url),
  };
}

/**
 * Reduce a configured tenant URL to its host for cross-request comparisons.
 *
 * @example
 * const settingsHost = getSafeSettingsHost(settings.general?.url);
 */
export function getSafeSettingsHost(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0) return null;

  try {
    return new URL(value).host.slice(0, MAX_DIAGNOSTIC_TEXT_LENGTH);
  } catch {
    return null;
  }
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object") return undefined;

  return value as Record<string, unknown>;
}

function isHomeSsrDiagnosticContext(
  value: unknown,
): value is HomeSsrDiagnosticContext {
  const contextRecord = asRecord(value);

  return (
    typeof contextRecord?.requestId === "string" &&
    contextRecord.loggedEvents instanceof Set
  );
}

function getRequestId(request: Request): string {
  const incomingRequestId =
    request.headers.get("x-request-id") ??
    request.headers.get("x-correlation-id");

  if (incomingRequestId && /^[A-Za-z0-9._:-]{1,128}$/.test(incomingRequestId)) {
    return incomingRequestId;
  }

  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function sanitizeHeaderValue(value: string | null): string | null {
  if (!value) return null;

  return value.trim().slice(0, MAX_DIAGNOSTIC_TEXT_LENGTH);
}

function sanitizeDiagnosticText(
  value: unknown,
  maxLength: number = MAX_DIAGNOSTIC_TEXT_LENGTH,
): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;

  return value
    .replace(/Bearer\s+[^\s"']+/gi, "Bearer [REDACTED]")
    .replace(
      /\b(authorization|cookie|access[_-]?token|refresh[_-]?token)\b\s*[:=]\s*[^\s,;]+/gi,
      "$1=[REDACTED]",
    )
    .replace(
      /([?&](?:access[_-]?token|refresh[_-]?token|token|authorization)=)[^&#\s]+/gi,
      "$1[REDACTED]",
    )
    .slice(0, maxLength);
}

function getSafeIdentifier(value: unknown): string | number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  if (typeof value !== "string" || value.length === 0) return null;

  return value.slice(0, 128);
}

function getSafeType(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0) return null;

  return value.slice(0, 64);
}

function getSafeNumber(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;

  return value;
}

function getSafeRequestPath(value: unknown): string | undefined {
  if (typeof value !== "string" || value.length === 0) return undefined;

  try {
    return new URL(value, "http://diagnostic.invalid").pathname.slice(
      0,
      MAX_DIAGNOSTIC_URL_LENGTH,
    );
  } catch {
    return undefined;
  }
}
